import { useState, useCallback } from 'react';
import moment from 'moment';
import { validateAuditForm, initialFormState } from '../utils/auditFormValidation';
import { SOC2FrameworkType } from '../../../../utils/enums';

export const useAuditForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const calculateEndDate = useCallback(() => {
    if (!formData.audit_start_date) return "";

    // For SOC 2 Type 1, end date = start date (point-in-time audit)
    const isSOC2Type1 = formData.framework_base === "SOC2" && formData.framework_type === "type1";
    if (isSOC2Type1) {
      return formData.audit_start_date;
    }

    if (formData.audit_window_months === "custom") {
      return formData.custom_end_date;
    }

    const months = parseInt(formData.audit_window_months);
    return moment(formData.audit_start_date).add(months, "months").format("YYYY-MM-DD");
  }, [formData.audit_start_date, formData.audit_window_months, formData.custom_end_date, formData.framework_base, formData.framework_type]);

  const handleChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    if (field === "audit_type") {
      setFormData((prev) => ({
        ...prev,
        auditors: [],
      }));
      if (errors.audit_plan_file) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.audit_plan_file;
          return next;
        });
      }
    }
  }, [errors]);

  const handleEvidenceWindowChange = useCallback((event, newValue) => {
    if (newValue !== null) {
      setFormData((prev) => {
        const updated = {
          ...prev,
          audit_window_months: newValue,
        };
        if (newValue !== "custom" && prev.audit_window_months === "custom") {
          updated.custom_end_date = "";
        }
        return updated;
      });
      
      if (errors.audit_window_months || errors.custom_end_date) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.audit_window_months;
          delete newErrors.custom_end_date;
          return newErrors;
        });
      }
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = validateAuditForm(formData, calculateEndDate);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, calculateEndDate]);

  const resetForm = useCallback(() => {
    setFormData(initialFormState);
    setErrors({});
  }, []);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    calculateEndDate,
    handleChange,
    handleEvidenceWindowChange,
    validateForm,
    resetForm,
  };
};
